import { optimizePostMediaFile } from './openDiscussions/lib/optimizePostMedia';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { getStoredActiveProfileMode } from '../profileIdentity/activeProfileMode';
import { resolveAppApiOrigin } from '../../utils/appApiOrigin';

function apiOrigin() {
  return resolveAppApiOrigin() || process.env.REACT_APP_BASE_URL || '';
}

function uploadFetchErrorMessage(status, data, responseText = '', fallback = 'Upload failed') {
  if (status === 413) {
    if (data?.message) return data.message;
    const html = String(responseText || '');
    if (/<html/i.test(html) || /Request Entity Too Large/i.test(html)) {
      return (
        'Upload blocked (413): request body too large for Cloudflare or nginx. ' +
        'In Cloudflare: Network → Maximum upload size → 100 MB. ' +
        'On nginx: client_max_body_size 85m; inside location /api/.'
      );
    }
    return 'File is too large for the server (max 80 MB).';
  }
  if (status === 429) {
    return data?.message || 'Too many uploads. Please wait and try again.';
  }
  if (status === 524 || status === 504) {
    return 'Upload timed out at the CDN (Cloudflare ~100s limit). Try a smaller clip or contact ops to raise the proxy timeout.';
  }
  return data?.message || fallback;
}

/** XHR multipart upload — supports progress + long timeout (images and local dev). */
function uploadDiscussionMediaViaProxy(file, { onProgress } = {}) {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append('media', file, file.name || 'media');
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiOrigin()}/api/knowledge/discussions/upload-media`);
    xhr.withCredentials = true;
    xhr.timeout = 15 * 60 * 1000;
    xhr.upload.onprogress = (event) => {
      if (!onProgress || !event.lengthComputable) return;
      onProgress({
        loaded: event.loaded,
        total: event.total,
        percent: Math.min(100, Math.round((event.loaded / event.total) * 100)),
      });
    };
    xhr.onload = () => {
      let data = {};
      try {
        data = JSON.parse(xhr.responseText || '{}');
      } catch {
        data = {};
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(data);
        return;
      }
      const err = new Error(
        uploadFetchErrorMessage(xhr.status, data, xhr.responseText || '', xhr.statusText || 'Upload failed')
      );
      err.status = xhr.status;
      reject(err);
    };
    xhr.onerror = () => {
      reject(
        new Error(
          'Upload could not reach the server (connection lost). Large videos can take several minutes — keep this tab open and retry if needed.'
        )
      );
    };
    xhr.ontimeout = () => {
      reject(new Error('Upload timed out after 15 minutes. Try a smaller video or a faster connection.'));
    };
    xhr.send(fd);
  });
}

function isDiscussionVideoFile(file) {
  const type = String(file?.type || '').toLowerCase();
  if (type.startsWith('video/')) return true;
  return /\.(mp4|webm|mov|m4v|3gp|avi)(\?|#|$)/i.test(String(file?.name || ''));
}

/** Same-origin chunked upload — avoids R2 CORS/CSP and Cloudflare single-request timeout. */
const VIDEO_CHUNK_UPLOAD_CONCURRENCY = 4;

function uploadDiscussionVideoChunk(file, uploadId, chunkSize, chunkIndex, { onChunkProgress } = {}) {
  const start = chunkIndex * chunkSize;
  const end = Math.min(file.size, start + chunkSize);
  const slice = file.slice(start, end);
  const chunkBytes = end - start;
  const fd = new FormData();
  fd.append('uploadId', uploadId);
  fd.append('chunkIndex', String(chunkIndex));
  fd.append('chunk', slice, `chunk-${chunkIndex}`);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiOrigin()}/api/knowledge/discussions/upload-media/chunk`);
    xhr.withCredentials = true;
    xhr.timeout = 5 * 60 * 1000;
    xhr.upload.onprogress = (event) => {
      if (!onChunkProgress || !event.lengthComputable) return;
      onChunkProgress(chunkIndex, event.loaded, chunkBytes);
    };
    xhr.onload = () => {
      let data = {};
      try {
        data = JSON.parse(xhr.responseText || '{}');
      } catch {
        data = {};
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        if (onChunkProgress) onChunkProgress(chunkIndex, chunkBytes, chunkBytes);
        resolve(data);
        return;
      }
      const err = new Error(
        uploadFetchErrorMessage(xhr.status, data, xhr.responseText || '', xhr.statusText || 'Chunk upload failed')
      );
      err.status = xhr.status;
      reject(err);
    };
    xhr.onerror = () => reject(new Error('Chunk upload connection lost'));
    xhr.ontimeout = () => reject(new Error('Chunk upload timed out'));
    xhr.send(fd);
  });
}

async function uploadDiscussionVideoChunked(file, { onProgress } = {}) {
  const initRes = await fetch(`${apiOrigin()}/api/knowledge/discussions/upload-media/chunk/init`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      contentType: file.type || 'video/mp4',
      fileName: file.name || 'video.mp4',
      fileSize: file.size,
    }),
  });
  const init = await initRes.json().catch(() => ({}));
  if (!initRes.ok || !init?.uploadId) {
    const err = new Error(init?.message || initRes.statusText || 'Could not start upload');
    err.status = initRes.status;
    throw err;
  }

  const { uploadId, chunkSize, totalChunks } = init;
  const chunkLoaded = new Array(totalChunks).fill(0);

  const reportProgress = () => {
    if (!onProgress) return;
    const loaded = chunkLoaded.reduce((sum, n) => sum + n, 0);
    onProgress({
      loaded,
      total: file.size,
      percent: Math.min(99, Math.round((loaded / file.size) * 99)),
    });
  };

  const onChunkProgress = (chunkIndex, loaded, total) => {
    chunkLoaded[chunkIndex] = Math.min(loaded, total);
    reportProgress();
  };

  let nextChunkIndex = 0;
  const workers = Array.from({ length: Math.min(VIDEO_CHUNK_UPLOAD_CONCURRENCY, totalChunks) }, async () => {
    while (true) {
      const chunkIndex = nextChunkIndex;
      nextChunkIndex += 1;
      if (chunkIndex >= totalChunks) break;
      await uploadDiscussionVideoChunk(file, uploadId, chunkSize, chunkIndex, { onChunkProgress });
    }
  });
  await Promise.all(workers);

  if (onProgress) onProgress({ loaded: file.size, total: file.size, percent: 99 });

  const completeRes = await fetch(`${apiOrigin()}/api/knowledge/discussions/upload-media/chunk/complete`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ uploadId }),
  });
  const data = await completeRes.json().catch(() => ({}));
  if (!completeRes.ok) {
    const err = new Error(data?.message || completeRes.statusText || 'Upload finalize failed');
    err.status = completeRes.status;
    throw err;
  }
  if (onProgress) onProgress({ loaded: file.size, total: file.size, percent: 100 });
  return data;
}

export function uploadDiscussionMediaFile(file, { onProgress } = {}) {
  return optimizePostMediaFile(file).then((optimizedFile) => {
    if (isDiscussionVideoFile(optimizedFile)) {
      return uploadDiscussionVideoChunked(optimizedFile, { onProgress });
    }
    return uploadDiscussionMediaViaProxy(optimizedFile, { onProgress });
  });
}

export async function post(path, body, signal) {
  const res = await fetch(`${apiOrigin()}/api${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
    signal,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || res.statusText || 'Request failed');
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const knowledgeApi = {
  officialList: () => post('/knowledge/official/list', {}),
  officialGet: (docKey) => post('/knowledge/official/get', { docKey }),
  search: (q, signal) => post('/knowledge/search', { q }, signal),
  communityList: (mineOnly, opts = {}) =>
    post('/knowledge/community/list', {
      mineOnly: mineOnly === true,
      ...(opts.skip != null ? { skip: opts.skip } : {}),
      ...(opts.limit != null ? { limit: opts.limit } : {}),
      ...(opts.authorUserId != null ? { authorUserId: String(opts.authorUserId).trim() } : {}),
    }),
  communityGet: (slug) => post('/knowledge/community/get', { slug }),
  communityCreate: (payload) => post('/knowledge/community/create', payload),
  communityUpdate: (payload) => post('/knowledge/community/update', payload),
  uploadCommunityCover: async (file) => {
    const fd = new FormData();
    fd.append('cover', file);
    const res = await fetch(`${apiOrigin()}/api/knowledge/community/upload-cover`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.message || res.statusText || 'Upload failed');
      err.status = res.status;
      throw err;
    }
    return data;
  },
  moderationList: () => post('/knowledge/moderation/list', {}),
  moderationDecide: (slug, approve, note) => post('/knowledge/moderation/decide', { slug, approve, note }),
  mentionUsersSearch: (q, signal) =>
    post('/knowledge/mention-users/search', { q: String(q || '').trim() }, signal).then((d) => d.users || []),
  linkPreview: (url, signal) => post('/knowledge/link-preview', { url: String(url || '').trim() }, signal),
  commentsList: (slug, opts) =>
    post('/knowledge/comments/list', {
      slug,
      ...(opts && opts.rootSkip != null ? { rootSkip: opts.rootSkip } : {}),
      ...(opts && opts.rootLimit != null ? { rootLimit: opts.rootLimit } : {}),
      ...(opts && opts.rootsNewestFirst != null ? { rootsNewestFirst: opts.rootsNewestFirst } : {}),
    }),
  commentsSubtree: (slug, commentId) =>
    post('/knowledge/comments/subtree', { slug, commentId: String(commentId) }),
  commentsAdd: (slug, body, parentCommentId, flags = {}) =>
    post('/knowledge/comments/add', {
      slug,
      body,
      parentCommentId: parentCommentId || undefined,
      spoiler: Boolean(flags.spoiler),
      ageRestricted: Boolean(flags.ageRestricted),
      sensitivity: Boolean(flags.sensitivity),
      profileMode: flags.profileMode || getStoredActiveProfileMode(),
    }),
  commentsUpdate: (slug, commentId, body, flags = {}) =>
    post('/knowledge/comments/update', {
      slug,
      commentId,
      body,
      spoiler: Boolean(flags.spoiler),
      ageRestricted: Boolean(flags.ageRestricted),
      sensitivity: Boolean(flags.sensitivity),
    }),
  commentsDelete: (slug, commentId) => post('/knowledge/comments/delete', { slug, commentId }),
  commentReactionToggle: (slug, commentId, emoji) =>
    post('/knowledge/comments/reactions/toggle', { slug, commentId, emoji }),
  reactionsSummary: (slug) => post('/knowledge/reactions/summary', { slug }),
  reactionsToggle: (slug, type) => post('/knowledge/reactions/toggle', { slug, type }),
  reactionsEmojiToggle: (slug, emoji) => post('/knowledge/reactions/emoji/toggle', { slug, emoji }),
  topAuthors: () => post('/knowledge/stats/top-authors', {}),
  contactChain: (userId, snapshot) => post('/knowledge/author/contact-chain', { userId, snapshot }),
  kbNotificationsList: (limit) => post('/knowledge/notifications/list', { limit }),
  kbNotificationsMarkRead: (ids) => post('/knowledge/notifications/mark-read', { ids }),

  discussionsCategories: () => post('/knowledge/discussions/categories', {}),
  discussionsClubsList: (opts = {}) =>
    post('/knowledge/discussions/clubs/list', {
      profileMode: getStoredActiveProfileMode(),
      ...(opts.skip != null ? { skip: opts.skip } : {}),
      ...(opts.limit != null ? { limit: opts.limit } : {}),
      ...(opts.q ? { q: opts.q } : {}),
      ...opts,
    }),
  discussionsClubCreate: (payload) => post('/knowledge/discussions/clubs/create', payload),
  discussionsClubDetail: (opts = {}) =>
    post('/knowledge/discussions/clubs/detail', {
      profileMode: getStoredActiveProfileMode(),
      clubSlug: opts.clubSlug || opts.slug || '',
      ...(opts.topicsSkip != null ? { topicsSkip: opts.topicsSkip } : {}),
      ...(opts.topicsLimit != null ? { topicsLimit: opts.topicsLimit } : {}),
      ...(opts.topicsMode ? { topicsMode: opts.topicsMode } : {}),
      ...(opts.includeStats === false ? { includeStats: false } : {}),
      ...opts,
    }),
  discussionsClubUploadThumbnail: async (clubSlug, file) => {
    const fd = new FormData();
    fd.append('clubSlug', String(clubSlug || '').trim());
    fd.append('icon', file);
    const res = await fetch(`${apiOrigin()}/api/knowledge/discussions/clubs/upload-thumbnail`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.message || res.statusText || 'Upload failed');
      err.status = res.status;
      throw err;
    }
    return data;
  },
  discussionsClubJoin: (clubSlug) =>
    post('/knowledge/discussions/clubs/join', {
      clubSlug,
      profileMode: getStoredActiveProfileMode(),
    }),
  discussionsClubUpdateSettings: (payload) => post('/knowledge/discussions/clubs/update-settings', payload),
  discussionsClubMemberRestrictions: (payload) => post('/knowledge/discussions/clubs/member-restrictions', payload),
  discussionsClubMemberSearch: (payload, signal) =>
    post('/knowledge/discussions/clubs/member-search', payload, signal),
  discussionsClubLeave: (clubSlugOrOpts) => {
    const profileMode = getStoredActiveProfileMode();
    if (clubSlugOrOpts == null) return post('/knowledge/discussions/clubs/leave', { profileMode });
    if (typeof clubSlugOrOpts === 'string') {
      const clubSlug = clubSlugOrOpts.trim();
      return post('/knowledge/discussions/clubs/leave', clubSlug ? { clubSlug, profileMode } : { profileMode });
    }
    const o = clubSlugOrOpts;
    const body = {};
    if (o.clubId != null && String(o.clubId).trim() !== '') body.clubId = String(o.clubId).trim();
    if (o.clubSlug != null && String(o.clubSlug).trim() !== '') body.clubSlug = String(o.clubSlug).trim();
    body.profileMode = getStoredActiveProfileMode();
    return post('/knowledge/discussions/clubs/leave', body);
  },
  discussionsHomeSummary: () => post('/knowledge/discussions/home-summary', {}),
  discussionsList: (opts = {}) =>
    post('/knowledge/discussions/list', {
      profileMode: getStoredActiveProfileMode(),
      ...opts,
    }),
  discussionsHashtagExplore: (opts = {}) => post('/knowledge/discussions/hashtags/explore', opts),
  discussionsHashtagsList: (opts = {}) => post('/knowledge/discussions/hashtags/list', opts),
  discussionsCreate: (payload) => post('/knowledge/discussions/create', payload),
  discussionsUpdate: (payload) => post('/knowledge/discussions/update', payload),
  discussionsChartGet: (chartSlug) => post('/knowledge/discussions/chart/get', { chartSlug }),
  discussionsDelete: (slug) => post('/knowledge/discussions/delete', { slug }),
  discussionsUploadMedia: (file, opts) => uploadDiscussionMediaFile(file, opts),
  discussionsGet: (slug, opts = {}) =>
    post('/knowledge/discussions/get', {
      slug,
      ...(opts.includeSidebar ? { includeSidebar: true } : {}),
    }),
  discussionsThreadSidebar: (slug) => post('/knowledge/discussions/thread-sidebar', { slug }),
  discussionsVote: (slug, vote) =>
    post('/knowledge/discussions/vote', { slug, vote, profileMode: getStoredActiveProfileMode() }),
  discussionsReactionToggle: (slug, emoji) =>
    post('/knowledge/discussions/reactions/toggle', { slug, emoji }),
  discussionsViewLaterList: () => post('/knowledge/discussions/view-later/list', {}),
  discussionsViewLaterAdd: (slug) => post('/knowledge/discussions/view-later/add', { slug }),
  discussionsViewLaterRemove: (slug) => post('/knowledge/discussions/view-later/remove', { slug }),
  discussionsViewLaterThreads: () => post('/knowledge/discussions/view-later/threads', {}),
  discussionsHideFromFeedAdd: (slug) => post('/knowledge/discussions/hide-from-feed/add', { slug }),
  discussionsHideFromFeedList: () => post('/knowledge/discussions/hide-from-feed/list', {}),
  discussionsPreferencesGet: () => post('/knowledge/discussions/preferences/get', {}),
  discussionsPreferencesSet: (payload) => post('/knowledge/discussions/preferences/set', payload),
  discussionsDisable: (slug, payload) =>
    post('/knowledge/discussions/disable', typeof payload === 'string' ? { slug, reason: payload } : { slug, ...payload }),
  discussionsEnable: (slug) => post('/knowledge/discussions/enable', { slug }),
  discussionsCommentDisable: (discussionSlug, commentId, payload) =>
    post('/knowledge/discussions/comment/disable', {
      discussionSlug,
      commentId,
      ...(typeof payload === 'string' ? { reason: payload } : payload || {}),
    }),
  discussionsCommentEnable: (discussionSlug, commentId) =>
    post('/knowledge/discussions/comment/enable', { discussionSlug, commentId }),
  discussionsModerationMe: () => post('/knowledge/discussions/moderation/me', {}),
  discussionsReportSubmit: (body) => post('/knowledge/discussions/report/submit', body),
  discussionsModerationReports: (opts = {}) => post('/knowledge/discussions/moderation/reports', opts),
  discussionsModerationReportResolve: (reportId, status, moderatorNote) =>
    post('/knowledge/discussions/moderation/report/resolve', { reportId, status, moderatorNote }),
  discussionsModerationModerators: () => post('/knowledge/discussions/moderation/moderators', {}),
  discussionsModerationAddModerator: (nomineeUserId) =>
    post('/knowledge/discussions/moderation/moderators/add', { nomineeUserId }),
  discussionsModerationRemoveModerator: (moderatorUserId) =>
    post('/knowledge/discussions/moderation/moderators/remove', { moderatorUserId }),
  discussionsModerationUserSearch: (q, signal) =>
    post('/knowledge/discussions/moderation/users/search', { q }, signal),
  discussionsModerationNominationDecide: (nominationId, approve) =>
    post('/knowledge/discussions/moderation/nomination/decide', { nominationId, approve }),
  discussionsModerationUserRestrict: (userId, opts = {}) =>
    post('/knowledge/discussions/moderation/user/restrict', { userId, ...opts }),
  discussionsModerationUserRestrictClear: (userId) =>
    post('/knowledge/discussions/moderation/user/restrict/clear', { userId }),
  discussionsClubDisable: (body) => post('/knowledge/discussions/clubs/disable', body),
  discussionsClubEnable: (clubSlug) => post('/knowledge/discussions/clubs/enable', { clubSlug }),
  commentsVote: (slug, commentId, vote) =>
    post('/knowledge/comments/vote', {
      slug,
      commentId,
      vote,
      profileMode: getStoredActiveProfileMode(),
    }),
};

export async function uploadCoverPic(file, opts = {}) {
  const fd = new FormData();
  fd.append('coverPic', file);
  const profileMode = opts.profileMode || getStoredActiveProfileMode();
  if (profileMode) fd.append('profileMode', profileMode);
  const res = await fetch(`${apiOrigin()}/api/upload-cover-pic`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || res.statusText || 'Upload failed');
    err.status = res.status;
    throw err;
  }
  return data;
}

export const socialApi = {
  publicProfile: (opts = {}) => post('/social/public-profile', opts),
  profileByHandle: (opts = {}) => post('/social/profile-by-handle', opts),
  profileSearch: (opts = {}) => post('/social/profile-search', opts),
  feedPeopleSuggestions: (opts = {}) => post('/social/feed-people-suggestions', opts),
  followStatuses: (opts = {}) => post('/social/follow/statuses', opts),
  follow: (opts = {}) => post('/social/follow', opts),
  unfollow: (opts = {}) => post('/social/unfollow', opts),
  followRequests: (opts = {}) => post('/social/follow/requests', opts),
  respondFollowRequest: (opts = {}) => post('/social/follow/requests/respond', opts),
  contentAccessRequests: (opts = {}) => post('/social/content-access/requests', opts),
  respondContentAccess: (opts = {}) => post('/social/content-access/respond', opts),
  followers: (opts = {}) => post('/social/followers', opts),
  following: (opts = {}) => post('/social/following', opts),
};

function withViewerProfileMode(opts = {}) {
  return { viewerProfileMode: getStoredActiveProfileMode(), ...opts };
}

export const messagingApi = {
  conversations: (opts = {}) => post('/messaging/conversations', withViewerProfileMode(opts)),
  search: (opts = {}) => post('/messaging/search', withViewerProfileMode(opts)),
  messages: (opts = {}) => post('/messaging/messages', opts),
  send: (opts = {}) => post('/messaging/send', withViewerProfileMode(opts)),
  edit: (opts = {}) => post('/messaging/edit', opts),
  delete: (opts = {}) => post('/messaging/delete', opts),
  markRead: (opts = {}) => post('/messaging/read', opts),
  unreadCount: (opts = {}) => post('/messaging/unread-count', withViewerProfileMode(opts)),
  status: (opts = {}) => post('/messaging/status', withViewerProfileMode(opts)),
  block: (opts = {}) => post('/messaging/block', opts),
};

export { sanitizeHtml };

/** @deprecated Use sanitizeHtml — kept for existing imports during migration. */
export function stripScripts(html) {
  return sanitizeHtml(html);
}
